import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Watch,
  State,
  Event,
  EventEmitter,
  Method,
} from '@stencil/core';
import { toggleDisableChildNodes } from '../../utils/toggleDisableChildNodes';
import { Types } from './types';
import { closest } from '../../utils/domHelpers';
import { closestAll } from '../aha-menu/helpers';

// Watch for updates inside the light DOM. This way if an `<a>` inside an `<aha-menu-item>` is set to "active"/"selected", the parent `<aha-menu-item>` itself will be too.
const mo = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    const target = mutation.target as HTMLElement;
    isSelected(target.closest('aha-menu-item'));
  });
});

// Watch for updates inside the light DOM. If something in the child nodes is "selected" then set the entire parent <aha-menu-item> to be selected too.
function isSelected(host: any) {
  if (!host) return;
  Array.from(host.children).forEach((node: any) => {
    // Only look at the first immediate child to see if the aha-menu-item should be selected.
    if (!node.matches('aha-menu-item > *')) return;

    // If the first immediate child has a matching attribute/class, then make the aha-menu-item selected as well.
    if (
      node.hasAttribute('active') ||
      node.hasAttribute('current') ||
      node.hasAttribute('selected') ||
      node.classList.contains('active') ||
      node.classList.contains('current') ||
      node.classList.contains('selected')
    ) {
      host.selected = true;
    }
  });
}

window.addEventListener('keydown', e => {
  if (e.key !== 'Enter' || !document.activeElement) return;
  const target = e.composedPath()[0] as
    | (HTMLAnchorElement & { tagName: 'A' })
    | HTMLAhaMenuItemElement;

  if (target.tagName === 'A') {
    const menuItem = closest(target, 'aha-menu-item') as HTMLAhaMenuItemElement;
    if (menuItem && !menuItem.disabled) menuItem.selected = true;
  } else if (target.tagName === 'AHA-MENU-ITEM') {
    if (target.disabled) return;
    target.click();
  }
});

export interface AhaMenuItem extends HTMLAhaMenuItemElement {}

/** A component that behaves as an enhanced `<li>` component.
 *
 * The `aha-menu-item` can be a direct child of either the `aha-menu-content` component or the `aha-menu-group` component.
 *
 * When a user clicks on the menu item, it is switched to an "selected" state. Similarly, if an `aha-menu-item` has an immediate child node with a class/attribute of "active", "current", or "selected" the parent `aha-menu-item` will automatically become selected.
 *
 * Note there can be only one currently "selected" `aha-menu-item` per `aha-menu-content`.
 *
 * @slot DEFAULT - Contents that appear as child nodes of the `<aha-menu-item>` component.
 */
@Component({
  tag: 'aha-menu-item',
  styleUrl: 'aha-menu-item.css',
  shadow: true,
})
export class AhaMenuItem {
  /**
   * Triggers when the menu-item is selected.
   */
  @Event({
    eventName: 'aha-menu-item:selected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectEvent!: EventEmitter;

  /**
   * Triggers when the menu-item is deselected.
   */
  @Event({
    eventName: 'aha-menu-item:deselected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  deselectEvent!: EventEmitter;

  /**
   * Whether a menu item is currently selected or not.
   */
  @Prop({ reflect: true, mutable: true }) selected: boolean = false;
  @Watch('selected')
  private reactSelected() {
    if (this.selected) {
      // Set all other <aha-menu-item> elements to un-selected.
      const menuContent = closest(this.host, 'aha-menu-content');
      if (menuContent) {
        const menuItems =
          menuContent.querySelectorAll<HTMLAhaMenuItemElement>(':scope > aha-menu-item');
        menuItems.forEach(item => {
          if (item.selected && item !== this.host) {
            item.selected = false;
          }
        });
      }
      this.selectEvent.emit(this.host);
    } else {
      // Set the immediate child node to be un-selected, since its activation status may be dictated by `isSelected` method.
      const possibleSelectedElement = this.host.querySelector('*:first-child');
      if (possibleSelectedElement) {
        ['active', 'current', 'selected'].forEach(value => {
          possibleSelectedElement.removeAttribute(value);
          possibleSelectedElement.classList.remove(value);
        });
      }
      this.deselectEvent.emit(this.host);
    }
  }

  /**
   * The "selected" state is typically transitory, and the item becomes unselected when the menu-item mosueup action is complete. If `persistSelected` is set to true, the item will remain selected even after.
   */
  @Prop({ reflect: true, attribute: 'persist-selected' }) persistSelected: boolean = false;

  /**
   * Whether a menu item is disabled or not.
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Type of menu-item.
   */
  @Prop({ reflect: true }) type: Types | undefined = undefined;

  /** Prevent the parent menu from closing when this `<aha-menu-item>` is clicked on.
   * Note, you can also set a `data-prevent-close` on an `<aha-menu-item>` to prevent the menu from closing. This is allowed for historical reasons.
   */
  @Prop({ reflect: true, attribute: 'prevent-close' }) preventClose: boolean = false;

  /** Prevent the parent menu from closing any ancestor (non-immediate-parent) <aha-menu-content> when this `<aha-menu-item>` is clicked on.
   */
  @Prop({ reflect: true }) preventCloseAncestorMenu: boolean = false;

  // Whether or not an `<aha-menu-item>` has ONLY elements slotted as children (in which case, add padding to the child elements), or ONLY has text nodes slotted as children (in which case, add padding to the `<aha-menu-item>` itself). Largely exists to support existing usage of `<aha-menu-item>` in aha-develop.
  @State() childless: boolean = false;
  @Watch('childless')
  private markAsChildless() {
    this.childless
      ? this.host.setAttribute('childless', '')
      : this.host.removeAttribute('childless');
  }

  private observer: any;

  @Watch('disabled')
  disableChildren() {
    toggleDisableChildNodes(this.host);
  }

  connectedCallback() {
    this.observer = mo.observe(this.host, { childList: true, attributes: true, subtree: true });

    // Special handling for deprecated <aha-menu> components (ex. customer extensions) where <aha-menu-item>s
    // are not wrapped inside an <aha-menu-content>. In this case, we assume that it is a react-based menu
    // and fall back to `useDeprecatedRender` path.
    if (!this.host.closest('aha-menu-content') && !this.disabled) {
      this.host.tabIndex = 0;
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  componentWillLoad() {
    this.childless = !this.host.querySelector('*');
  }

  componentDidLoad() {
    if (this.disabled) this.disableChildren();
  }

  @Element() host!: HTMLAhaMenuItemElement;

  private handleSlotChange(event: any) {
    const host = event.target.getRootNode().host;
    isSelected(host);

    host.childless = !event.target.assignedElements().length;
  }

  // A variable to store the original `selected` value when mousedown/up events occur.
  private tempSelected: boolean = false;

  private handleMouseLeave(event: MouseEvent) {
    if (!this.persistSelected) {
      this.selected = this.tempSelected;
      this.tempSelected = false;
    }
  }

  private handleMouseDown(event: MouseEvent) {
    this.tempSelected = this.selected;
    this.selected = true;
  }

  private handleMouseUp(event: MouseEvent) {
    if (!this.persistSelected) {
      this.selected = this.tempSelected;
      this.tempSelected = false;
    }

    // --
    // -- Determine if the parent menu-content should be closed.
    // --

    // Add this check here, otherwise the test suite fails because the portal is not part of the test.
    if (!this.host) return;

    const elem = event.target as HTMLElement;

    // If the <aha-menu-item> itself has the `prevent-close` attribute set, then do not close the menu.
    if (this.preventClose || typeof this.host.dataset.preventClose !== 'undefined') return false;

    let paths = event.composedPath();

    // If anything in the event path is disabled, then do not close the menu;
    if (paths.find(node => (node as any).disabled)) return false;

    // We only care about the nodes we could have clicked on from the target to the closest `<aha-menu-item>`.
    const simplePath = paths.slice(0, paths.indexOf(this.host));
    const preventClose = simplePath.find((node: any) => {
      // If the node is not an element, we don't care about it.
      if (node.nodeType !== 1) return false;

      // If we click on or inside any of these elemests, do not close the parent menu.
      if (
        node.matches(
          'aha-menu, aha-field, aha-tooltip, aha-switch, select, textarea, input:not([type="radio"]), [contenteditable], .selectro-trigger',
        )
      )
        return true;

      // If we click on a <label> and that label is not associated with a radio button, then do not close the menu.
      if (node.matches('label') && node.querySelector('input:not([type="radio"])')) return true;

      return false;
    });

    if (preventClose) return;

    // Wait a tick, otherwise the menu may hide before all other event handlers have fired.
    requestAnimationFrame(() => {
      const ancestorMenus = closestAll(elem, 'aha-menu-content');
      let menuToClose;

      if (this.preventCloseAncestorMenu) {
        // If `preventCloseAncestorMenu` is true, then only close the immediate parent <aha-menu-content> and not the full stack of nested <aha-menu-content> elements.
        menuToClose = closest(elem, 'aha-menu-content');
      } else {
        // Hide all parent <aha-menu> elements when an element is clicked on. This happens by simply closing the top-most ancestor <aha-menu-content> element.
        menuToClose = ancestorMenus.slice(-1)[0];
      }
      // @ts-ignore
      if (menuToClose) menuToClose.hide();
    });
  }

  @State() customTabIndex: number | null = null;

  @Method()
  async setTabIndex(value: number | null) {
    this.customTabIndex = value;
  }

  private getTabIndex(): number | null {
    if (this.customTabIndex !== null) {
      return this.customTabIndex;
    }
    return this.childless ? 0 : null;
  }

  render() {
    return (
      <Host
        role="menuitem"
        onMouseDown={this.handleMouseDown}
        onMouseUp={this.handleMouseUp}
        onMouseLeave={this.handleMouseLeave}
        tabindex={this.getTabIndex()}
      >
        <slot onSlotchange={this.handleSlotChange}></slot>
      </Host>
    );
  }
}
